<template>
  <div id="preview_tab" class="my-6">
    <NotificationPopup
      v-if="this.widget.edited"
      status="warning"
      title="Changes Unpublished"
      text="To view your updates on the widget, please save and publish them."
      class="mb-6"
    />

    <div class="tab-wrapper-primary">
      <div class="tab-heading-primary">{{ changeCase('additional', ElementE.TITLE) }}</div>
      <div class="flex flex-col gap-4 p-6">

        <NotificationPopup
          v-if="!this.widget.edited"
          status="info"
          title="Your widget is ready for publication!"
          text="Copy and paste the embed code into your secure website to display the widget."
        />

        <div class="w-full pt-2">
          <div class="relative">
            <label class="label-primary flex gap-1" for="embed-code">
              {{ changeCase('embed code', ElementE.LABEL) }}
              <CopyIcon
                @click="copyEmbedCode"
                class="cursor-pointer hover:opacity-80"
              />
            </label>

            <textarea
              @click="selectAll"
              ref="textarea"
              v-model="embedCode"
              id="embed-code"
              class="input-primary"
              readonly
            />
          </div>
        </div>
      </div>
    </div>
    <div class="tab-wrapper-primary mt-6">
      <div class="tab-heading-primary flex items-center justify-between">
        <span>{{ changeCase('preview', ElementE.TITLE) }}</span>
        <div class="flex items-center rounded-md border border-neutral-25 overflow-hidden">
          <button
            @click="previewMode = 'desktop'"
            :class="[
              'flex items-center gap-1.5 px-3 py-1 text-xs font-medium transition-colors',
              previewMode === 'desktop'
                ? 'bg-neutral-100 text-white'
                : 'bg-white text-neutral-75 hover:bg-neutral-0'
            ]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
            Desktop
          </button>
          <button
            @click="previewMode = 'mobile'"
            :class="[
              'flex items-center gap-1.5 px-3 py-1 text-xs font-medium transition-colors',
              previewMode === 'mobile'
                ? 'bg-neutral-100 text-white'
                : 'bg-white text-neutral-75 hover:bg-neutral-0'
            ]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
            Mobile
          </button>
        </div>
      </div>

      <!-- Desktop preview -->
      <!--
        No `overflow-auto` here on purpose. The previewed widget renders its date picker
        inside its own shadow root and cannot teleport the menu out to <body> (all widget
        CSS is injected into the shadow root, so a teleported menu would be unstyled), so
        any scroll/hidden container on this side truncates the calendar. The picker flips
        upward when it is near the bottom of the viewport, which put it above this panel's
        top edge and cut it down to roughly one visible row.
        Wide widgets (e.g. the matrix) still scroll — <main> in DefaultLayout is the
        scroll container and handles both axes. <main> is itself still a clipping
        boundary, so a picker flipping upward near the bottom of a very long form can
        be truncated at that larger radius — accepted for now.
      -->
      <div v-if="previewMode === 'desktop'" class="flex flex-col gap-4 p-6">
        <div class="relative rounded-lg min-h-[700px]">
          <WidgetCompiled :api="api" :widget-id="widgets.id" />
        </div>
      </div>

      <!-- Mobile preview — iPhone 12/13/14 (375×812pt) with Safari chrome -->
      <div v-else class="flex justify-center p-6 bg-gray-100">
        <div class="phone-frame">
          <!-- iOS Status bar — 47px (iPhone notch devices) -->
          <div class="safari-status-bar">
            <span class="font-semibold">9:41</span>
            <!-- Notch -->
            <div class="safari-notch" />
            <div class="flex items-center gap-1">
              <!-- Signal bars -->
              <svg width="15" height="11" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0" y="9" width="3" height="3" rx="0.5" fill="white"/><rect x="4.5" y="6" width="3" height="6" rx="0.5" fill="white"/><rect x="9" y="3" width="3" height="9" rx="0.5" fill="white"/><rect x="13.5" y="0" width="3" height="12" rx="0.5" fill="white"/></svg>
              <!-- WiFi -->
              <svg width="14" height="11" viewBox="0 0 24 18" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M12 16.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM12 10c2.9 0 5.5 1.2 7.4 3.1l-2.1 2.1C15.8 13.8 14 13 12 13s-3.8.8-5.3 2.2l-2.1-2.1C6.5 11.2 9.1 10 12 10zm0-6.5c4.3 0 8.2 1.7 11 4.6l-2.1 2.1C18.5 7.8 15.4 6.5 12 6.5S5.5 7.8 3.1 10.2L1 8.1C3.8 5.2 7.7 3.5 12 3.5z"/></svg>
              <!-- Battery -->
              <svg width="25" height="11" viewBox="0 0 27 13" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="22" height="12" rx="2" stroke="white" stroke-opacity="0.35"/><rect x="2" y="2" width="19" height="9" rx="1" fill="white"/><path d="M24 4.5v4a2 2 0 000-4z" fill="white" fill-opacity="0.4"/></svg>
            </div>
          </div>

          <!-- Safari address bar — 50px -->
          <div class="safari-address-bar">
            <div class="safari-url-pill">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#8e8e93" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              <span class="text-[12px] text-[#8e8e93]">yoursite.com</span>
            </div>
          </div>

          <!-- Widget content area — 637px (812 - 47 - 50 - 44 - 34) -->
          <div class="phone-content">
            <WidgetCompiled :api="api" :widget-id="widgets.id" />
          </div>

          <!-- Safari bottom toolbar — 44px -->
          <div class="safari-toolbar">
            <!-- Back -->
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#007AFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            <!-- Forward -->
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#007AFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            <!-- Share -->
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#007AFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
            <!-- Bookmarks -->
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#007AFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/></svg>
            <!-- Tabs -->
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#007AFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><rect x="7" y="7" width="14" height="14" rx="2" ry="2"/></svg>
          </div>

          <!-- Home indicator — 34px -->
          <div class="safari-home-indicator">
            <div class="safari-home-indicator-bar" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import NotificationPopup from "@/components/notifications/NotificationPopup.vue";
import CopyIcon from "@/components/icons/CopyIcon.vue";
import {WidgetI} from "@/interfaces/interfaces";
import WidgetCompiled from '@/components/WidgetCompiled.vue';
import { getEmbedCode } from '@/common/helpers';
import { WIDGET_SCRIPT, API_URL } from '@/common/constants';
import { changeCase } from "../../helpers";
import { ElementE } from '../../enums';

export default defineComponent({
  name: "PreviewTab",
  components: { NotificationPopup, CopyIcon, WidgetCompiled },
  props: ['widgets', 'isSavedWidget'],
  emits:['updateWidget', 'copied'],
  data(){
    return {
      tmpWidgetId: '996873de-8656-4b64-bca9-806a7cfd3a20',
      widgetId: ' ' as string,
      widget:{} as WidgetI,
      embedCode: '' as string,
      api: '' as string,
      isFirstLoad: true as boolean,
      previewMode: 'desktop' as 'desktop' | 'mobile',

      changeCase: changeCase,
      ElementE: ElementE,
    }
  },
  methods: {
    copyEmbedCode: function() {
      navigator.clipboard.writeText(this.embedCode);
      this.$emit('copied');
    },
    selectAll() {
      (this.$refs.textarea as HTMLTextAreaElement).select();
    },
    previewTabHandler(widgets: WidgetI) {
      this.widget = widgets
      this.widgetId = widgets.id


      this.embedCode = getEmbedCode(widgets.id);
      const BASE_URL = `${window.location.protocol}//${window.location.host}`;
      if (this.isFirstLoad) {
        let widgetScript = document.createElement('script');
        let scriptSrc = WIDGET_SCRIPT;
        if (WIDGET_SCRIPT.startsWith('/')) {
          scriptSrc = `${BASE_URL}${WIDGET_SCRIPT}`;
        }
        widgetScript.setAttribute('src', scriptSrc);
        document.body.appendChild(widgetScript);
        this.isFirstLoad = false;
      }
      if (API_URL.startsWith('/')) {
        this.api = `${BASE_URL}${API_URL}`;
      } else {
        this.api = API_URL;
      }
    }
  },
  mounted() {
    this.previewTabHandler(this.widgets);
  },
  watch: {
    widgets: {
      handler(widgets: WidgetI) {
        this.previewTabHandler(widgets);
      }, deep: true
    }
  },
});
</script>

<style scoped>
/* Mobile device frame — 385×856px ≈ iPhone 14 (390pt) */
.phone-frame {
  width: 385px;
  height: 856px;
  border-radius: 2.5rem;
  border: 3px solid #1f2937;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  background: #fff;
}

/* iOS status bar */
.safari-status-bar {
  height: 44px;
  min-height: 44px;
  background: #000;
  color: #fff;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0 14px 4px;
  font-size: 14px;
  position: relative;
}

/* Notch cutout */
.safari-notch {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 32px;
  background: #000;
  border-radius: 0 0 18px 18px;
}

/* Safari address bar */
.safari-address-bar {
  height: 46px;
  min-height: 46px;
  background: #f2f2f7;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
}

.safari-url-pill {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  border-radius: 10px;
  padding: 6px 12px;
  min-width: 180px;
  justify-content: center;
}

/* Content area — 856 - 44 - 46 - 40 - 32 = 694px */
.phone-content {
  height: 694px;
  min-height: 0;
  overflow-y: auto;
  padding: 0;
}

/* Safari bottom toolbar */
.safari-toolbar {
  height: 40px;
  min-height: 40px;
  background: #f2f2f7;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0 8px;
  border-top: 0.5px solid #c6c6c8;
}

/* Home indicator area */
.safari-home-indicator {
  height: 32px;
  min-height: 32px;
  background: #f2f2f7;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 8px;
}

.safari-home-indicator-bar {
  width: 134px;
  height: 5px;
  background: #1f2937;
  border-radius: 3px;
}
</style>
